import * as ImageSize from "utils/ImageSize";
import defaults from "lodash/defaults";
import getPixelRatio from "utils/getPixelRatio";
import queryString from "query-string";
import extractImageUrl from "./extractImageUrl";
import generateLocalFilenameFromUrl from "./generateLocalFilenameFromUrl";

const isOffline = typeof window !== "undefined" && !!window.offline;

// Read the DOM to get the preload image's fastly parameters and return the
// order of those parameters. We'll use these when we order parameters for all
// preloaded images.
let cachedQueryeParamsSortOrder: string[] | undefined;
function readQueryParamSortOrder() {
  if (cachedQueryeParamsSortOrder) return cachedQueryeParamsSortOrder;

  if (typeof document === "undefined") {
    return [];
  }

  const imageElement = document.querySelector<HTMLImageElement>(
    "#loading-image > img",
  );
  if (!imageElement) {
    return [];
  }

  const src = imageElement.src;
  cachedQueryeParamsSortOrder =
    src
      ?.match(/\?(.+)$/)?.[1]
      ?.split("&")
      ?.map((keyValuePair) => keyValuePair.replace(/=.+/, "")) ?? [];

  return cachedQueryeParamsSortOrder;
}

export function generateImageLink(
  url: string,
  options: {
    height?: number | undefined;
    width?: number | undefined;
    size?: (typeof ImageSize)[keyof typeof ImageSize] | undefined;
  },
) {
  // Bail early if we're passed a data url.
  if (url.startsWith("data")) {
    if (isOffline) {
      return url;
    }

    throw new Error(
      "data url was passed to `generateImageLink` instead of an actual URL. This indicates a bug in the player.",
    );
  }

  options = defaults({ isMobile: false }, options || {}, {
    size: ImageSize.LARGE,
  });

  if (url.length > 1024) {
    throw new Error(
      `URL is too long (${url.length} characters); this will cause a REGEXDOS`,
    );
  }

  url = extractImageUrl(url);
  if (isOffline) {
    return generateLocalFilenameFromUrl(url);
  }

  switch (options.size) {
    case ImageSize.SMALL:
      options.height = 1080 / 4;
      break;
    case ImageSize.MEDIUM:
      options.height = 1080 / 2;
      break;
    case ImageSize.LARGE:
    case ImageSize.EXTRA_LARGE:
      options.height = 896;
      break;
    default:
      throw new TypeError(
        `Invalid size included for generateImageLink. Expected exported constant of ${Object.values(
          ImageSize,
        )}`,
      );
  }

  const urlOptions = {
    auto: "webp",
    dpr: getPixelRatio(),
    format: "jpg",
    height: options.height,
    optimize: "low",
  };

  // Replace old fastly parameters; sometimes we run these through more than
  // once
  url = url.replace(/\?.+/, "");

  if (url.match(/s3.amazonaws/)) {
    // Replace s3 CDN content with verse route through `/av`
    url = `/av/${url.replace(/^(https?:)?\/\/.+?\//, "")}`;
  } else if (url.includes(".com")) {
    // Replace anything going to a `.com` (that wasn't a CDN) with a relative
    // route. This will ensure that local development will point to wherever
    // we're proxying requests against
    url = `/av/${url.replace(/^https?:\/\/.+?\/av\//, "")}`;
  }

  const order = readQueryParamSortOrder();
  const result = `${url}?${queryString.stringify(urlOptions, {
    sort: (a: string, b: string) => order.indexOf(a) - order.indexOf(b),
  })}`;

  return result;
}

export default generateImageLink;
